import TreeSelectComponent from "components/atoms/select/treeSelect/TreeSelectComponent";
import SearchInput from "components/atoms/input/search/SearchInput";
import { Text, UnderlineText } from "components/atoms/text/Text";
import TinyButton from "components/atoms/buttons/TinyButton";
import { COLORS } from "styles/colors";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import * as s from "../FilterStyled";
import { DashboardFilterAccountAndUser } from "api/interfaces/dashboardInterface.interface";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  checkEmpty,
  flattenArray,
  matchStringSearchKeyword,
  renameArrayKeys,
} from "utils/functions";
import { getFilterAccountUsers } from "api/dashboardAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useQuery } from "react-query";
import { useQueryClient } from "react-query";
import { MapFilter, setAccounts } from "redux/reducers/map/mapFilter";
import { TreeDataNode } from "antd";
import { TreeProps } from "antd/lib";
import { EventDataNode } from "antd/lib/tree";
import * as mediaQuery from "components/MediaQuery";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { getTreeCheckState, getVisibleChildKeys } from "utils/DashboardUtil";

type Props = {
  isOpen: boolean;
  onApplyAccount: () => void;
  onChangeFiltering: (name: string, value: boolean) => void;
  onChangeFilterName: (changeFilterName: string) => void;
};
// 키 이름을 변경할 매핑
const keyMapping = {
  id: "key",
  parentId: "parentId",
  name: "title",
  level: "level",
  parentLevel: "parentLevel",
  userGroups: "userGroups",
  isSelected: "isSelected",
  subRows: "children",
};

// accountFilter Type으로 변경
const changeAccountFilter = (item: DashboardFilterAccountAndUser[]) => {
  // console.log(item);
  if (item && item.length > 0) {
    return item.map((select: DashboardFilterAccountAndUser) => {
      return {
        isSelected: select.isSelected ? select.isSelected : true,
        accountId: select.id,
        level: select.level,
        // userGroups: tempUserGroups,
      };
    });
  } else return [];
};
function getRepresentativeNames(
  tree: TreeDataNode[],
  selection: React.Key[]
): string[] {
  // console.log(tree, selection);
  let selectedNames: string[] = [];
  const topLevelNode = tree.find((node) => selection.includes(node.key));
  if (topLevelNode) {
    selectedNames.push(topLevelNode.title as string);
  } else {
    if (tree[0] && tree[0].children) {
      tree[0].children.forEach((node: TreeDataNode) => {
        if (node.children && node.children.length > 0) {
          // 상위 노드가 선택되었는지 확인
          const isParentSelected = node.children.every((child) =>
            selection.includes(child.key)
          );

          if (isParentSelected) {
            selectedNames.push(node.title as string);
          }
          // 상위노드 선택X 하위 선택됐다면
          else {
            node.children.forEach((nodeChild) => {
              if (selection.includes(nodeChild.key))
                selectedNames.push(nodeChild.title as string);
            });
          }
        } else if (selection.includes(node.key)) {
          selectedNames.push(node.title as string);
        }
      });
    }
  }

  // console.log(selectedNames, "selectedNames");
  return selectedNames;
}
const filterDashboardTree = (
  tree: DashboardFilterAccountAndUser[],
  searchString: string
): DashboardFilterAccountAndUser[] => {
  const tempAccountTree = tree.reduce((filteredTree, node) => {
    const newNode: DashboardFilterAccountAndUser = {
      ...node,
      subRows: node.subRows
        ? filterDashboardTree(node.subRows, searchString)
        : undefined,
    };

    if (
      matchStringSearchKeyword(newNode.name, searchString) ||
      (newNode.subRows && newNode.subRows.length > 0)
    ) {
      filteredTree.push(newNode);
    }

    return filteredTree;
  }, [] as DashboardFilterAccountAndUser[]);

  return tempAccountTree;
};

const AccountFilter = (props: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const filter: MapFilter = useAppSelector((state) => state.mapFilter);

  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([
    selectedAccount.accountId,
  ]);
  const [searchInput, setSearchInput] = useState<string>("");

  const onChangeSearch = useCallback(
    (e: any) => {
      setSearchInput(e);
    },
    [checkedKeys]
  );

  const onResetFilter = () => {
    setSearchInput("");
    queryClient.invalidateQueries("searchMapFilterResult");
    setCheckedKeys([]);
  };

  const onApply = useCallback(() => {
    props.onApplyAccount();
  }, []);

  const onCheck: TreeProps["onCheck"] = (tempCheck, info) => {
    const nodeKey = info?.node?.key;

    if (isSearchMode && nodeKey != null) {
      const descendants = getVisibleChildKeys(treeData, nodeKey);
      if (descendants.length > 0) {
        setCheckedKeys((prev) =>
          info.checked
            ? [...prev, ...descendants.filter((k) => !prev.includes(k))]
            : prev.filter((k) => !descendants.includes(k))
        );
        return;
      }
    }

    const nextCheckedKeys = (
      Array.isArray(tempCheck) ? tempCheck : (tempCheck as any)?.checked
    ) as React.Key[];
    setCheckedKeys(nextCheckedKeys ?? []);
  };

  const onExpand = (
    expandedKeys: React.Key[],
    info: {
      node: EventDataNode<TreeDataNode>;
      expanded: boolean;
      nativeEvent: MouseEvent;
    }
  ) => {
    setExpandedKeys(expandedKeys);
  };

  // account list 구하기
  const { data, refetch } = useQuery(
    ["getAccountUser", selectedAccount.accountId, props.isOpen],
    () =>
      getFilterAccountUsers({
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 0,
      staleTime: 60000, // 1분
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        setExpandedKeys([selectedAccount.accountId]);
      },
      onError: (e: any) => {
        console.log(e, "error");
      },
    }
  );

  const treeData = useMemo(() => {
    if (data && data.result) {
      return renameArrayKeys(
        filterDashboardTree([data.result], searchInput),
        keyMapping
      );
    }

    return [];
  }, [data, searchInput]);
  const flatAccounts = useMemo(
    () => (data?.result ? flattenArray([data.result]) : []),
    [data]
  );

  const accountList = useMemo(() => {
    if (data?.result) {
      return changeAccountFilter(flattenArray([data.result ?? {}]));
    }

    return [];
  }, [data]);
  const isSearchMode = !checkEmpty(searchInput);

  const treeCheckedKeys = useMemo(() => {
    if (!isSearchMode) return checkedKeys;
    return getTreeCheckState(treeData, checkedKeys);
  }, [checkedKeys, treeData, isSearchMode]);

  useStompDebouncedRefetch({
    stompKey: ["organizationListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  const titleRendering = (nodeData: any) => {
    return (
      <s.AccountFilterItem level={nodeData.level}>
        {nodeData.title}
      </s.AccountFilterItem>
    );
  };

  useEffect(() => {
    const checkedAccountList = accountList.map((account) => {
      const isSelected = checkedKeys.includes(account.accountId);

      return {
        ...account,
        isSelected,
      };
    });

    let isFilter = checkedKeys.length === 0;
    props.onChangeFiltering("accounts", !isFilter);

    if (isFilter) {
      // console.log("111");
      props.onChangeFilterName("Organization");
      dispatch(setAccounts([]));
    } else {
      // console.log("222");
      let tempFilterName: string = "Organization";
      let tempRepresentativeNamesArray = getRepresentativeNames(
        treeData,
        checkedKeys
      );

      tempFilterName =
        tempRepresentativeNamesArray.length > 1
          ? `${tempRepresentativeNamesArray.length} Organizations`
          : tempRepresentativeNamesArray[0];

      props.onChangeFilterName(tempFilterName);
      dispatch(setAccounts(checkedAccountList!));
    }
  }, [treeData, checkedKeys, accountList]);

  useEffect(() => {
    setSearchInput("");
    setCheckedKeys([]);
  }, [filter.keyword]);

  return (
    <Wrapper>
      <s.Container>
        <s.Title>
          <Text fontSize={14} bold>
            Organizations
          </Text>
          <SearchInput
            map
            onClickSearch={onChangeSearch}
            keyword={searchInput}
            onClear={() => onChangeSearch("")}
          />
        </s.Title>

        <s.AccountContents>
          <TreeSelectComponent
            treeData={treeData}
            checkedKeys={checkedKeys}
            expandedKeys={expandedKeys}
            onCheck={onCheck}
            onExpand={onExpand}
            titleRender={titleRendering}
            height={280}
            searchKeyword={searchInput}
          />
        </s.AccountContents>

        <s.Reset>
          <UnderlineText
            color={COLORS.LIGHTGRAY}
            fontSize={12}
            onClick={onResetFilter}
            data-testid="dashboard-map-account-underline-text-reset-filter"
          >
            Clear
          </UnderlineText>
          <TinyButton
            label="Done"
            onClickBtn={onApply}
            data-testid="dashboard-map-account-tiny-button-apply"
          />
        </s.Reset>
      </s.Container>
    </Wrapper>
  );
};

export default AccountFilter;

const Wrapper = styled.div`
  position: relative;

  ${mediaQuery.isDefault} {
    min-width: 280px;
    max-width: 280px;
  }
`;
